import useScribeDetailQuery from "features/medical-scribe/medical-scribes-section/scribes-list/scribe-details-dialog/view-scribe-details/hooks/use-scribe-detail-query.hook";
import useScribeDetailTabs, {
  getScribeDetailsKey
} from "features/medical-scribe/medical-scribes-section/scribes-list/scribe-details-dialog/view-scribe-details/hooks/use-scribe-detail-tabs.hook";
import type { AmbientScribeTemplateType } from "services/types/ambient-scribe-template.type";
import type { AmbientScribe } from "services/types/ambient-scribe.type";

/**
 * What Insert would write, and from which tab: the scribe details' own read of the selected
 * recording, and their tab state, which this dialog owns so its button can follow the open tab.
 *
 * The read uses the details' args exactly (one cache entry, one request), so Insert writes what
 * the details show — not the list's copy, which can predate a regeneration. The list's row stands
 * in only until the details have loaded this recording.
 *
 * RTK keeps the last `data` while a new arg loads. That copy is used only when it is this
 * recording: after a pick it is the previous recording's and must be ignored, but when the
 * details' `?query=` changes for the same recording (a late feature read) it is exactly what
 * the details keep showing. `currentData` would go blank there, reopen the tab setup and move
 * the user off their tab.
 */
export default function useInsertScribeSource({
  selectedScribe,
  templateType,
  audiologyReportId
}: {
  selectedScribe: AmbientScribe | null;
  templateType: AmbientScribeTemplateType | undefined;
  audiologyReportId: number | undefined;
}) {
  const scribeId = selectedScribe ? Number(selectedScribe.id) : undefined;
  const detail = useScribeDetailQuery({
    scribeId,
    templateType,
    audiologyReportId
  });
  // Number() because AmbientScribe.id is number | string.
  const detailScribe =
    detail.remote != null && Number(detail.remote.id) === scribeId
      ? detail.remote
      : undefined;
  const tabs = useScribeDetailTabs({
    remote: detailScribe,
    isLoading: detail.isLoading,
    isFetching: detail.isFetching,
    scribeKey: getScribeDetailsKey(scribeId, undefined),
    isAudiologyMode: detail.isAudiologyMode,
    isAvsEnabled: detail.isAvsEnabled,
    isConsultDebriefEnabled: detail.isConsultDebriefEnabled
  });

  return {
    effectiveScribe: detailScribe ?? selectedScribe,
    tabs
  };
}
